/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Skeleton, IconButton, Tooltip, Divider, Fade } from '@mui/material';
import { ZoomIn, ZoomOut, Refresh } from '@mui/icons-material';
import { Trash } from '@/components/icons';
import Typography from '@/components/typography/typography';

interface HtmlThumbnailPreviewProps {
  template: any;
  width?: number | string; 
  height?: number;
  scale?: number;
  showControls?: boolean;
  onClick?: (template: any) => void;
  selectedTemplateId?: string;
  isFetching?: boolean;
  onDelete?: (template: any) => void;
  normalView?: boolean;
  minScale?: number;
  maxScale?: number;
  scaleStep?: number;
  loadingDelay?: number;
  refreshDelay?: number;
}

const HtmlThumbnailPreviewFull: React.FC<HtmlThumbnailPreviewProps> = ({
  template,
  width = '100%',
  height = 800,
  scale = 0.25,
  showControls = false,
  onClick = null,
  selectedTemplateId = '',
  isFetching = false,
  onDelete,
  normalView = false,
  minScale = 0.1,
  maxScale = 1,
  scaleStep = 1.2,
  loadingDelay = 500,
  refreshDelay = 300,
}) => {
  const [isLoading, setIsLoading] = useState(isFetching || true);
  const [currentScale, setCurrentScale] = useState(scale);
  const [error, setError] = useState<null | string>(null);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const loadingTimeoutRef = useRef<any>(null);
  const refreshTimeoutRef = useRef<any>(null);

  const [containerPixelWidth, setContainerPixelWidth] = useState<number>(typeof width === 'number' ? width : 800);

  // Measure container width for responsive scaling
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        setContainerPixelWidth(newWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const scaledWidth = containerPixelWidth / currentScale;
  const scaledHeight = height / currentScale;

  useEffect(() => {
    return () => {
      clearTimeout(loadingTimeoutRef.current);
      clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => setIsLoading(false), loadingDelay);
    return () => clearTimeout(loadingTimeoutRef.current);
  }, [template.contents, loadingDelay]);

  useEffect(() => setIsLoading(isFetching), [isFetching]);

  const handleZoomIn = useCallback(() => setCurrentScale(prev => Math.min(prev * scaleStep, maxScale)), [scaleStep, maxScale]);
  const handleZoomOut = useCallback(() => setCurrentScale(prev => Math.max(prev / scaleStep, minScale)), [scaleStep, minScale]);
  const handleRefresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => setIsLoading(false), refreshDelay);
  }, [refreshDelay]);

  const handleClick = useCallback(() => {
    if (onClick && !isLoading) onClick(template);
  }, [onClick, template, isLoading]);

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onDelete && !template?.is_default) onDelete(template);
  }, [onDelete, template]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  if (isLoading) return (
    <Box sx={{ width, height, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
    </Box>
  );

  if (error) return (
    <Box sx={{
      width, height, borderRadius: 1, overflow: 'hidden', border: '1px solid',
      borderColor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'error.light', color: 'error.contrastText'
    }}>
      Failed to load template
    </Box>
  );

  const isSelected = selectedTemplateId === template._id;
  const canZoomIn = currentScale < maxScale;
  const canZoomOut = currentScale > minScale;
  const canDelete = !template?.is_default;

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width, marginInline: 'auto', display: 'inline-block' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        onClick={handleClick}
        sx={(theme) => ({
          width: '100%',
          height,
          marginBlock: 0,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#fff',
          transition: 'all 0.2s ease-in-out',
          '&:hover': onClick ? { boxShadow: '0 4px 16px rgba(0,0,0,0.15)', transform: 'translateY(-1px)' } : {},
        })}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center', 
            alignItems: 'center',  
            overflow: 'auto',
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={{
              transform: `scale(${currentScale})`,
              transformOrigin: 'top left',
              pointerEvents: 'none',
              userSelect: 'none',
              width: containerPixelWidth,
              height: height,
            }}
          >
            <Box
              ref={contentRef}
              sx={{
                width: '100%',
                height: '100%',
                '& *': { maxWidth: '100% !important', boxSizing: 'border-box !important' },
                '& body': { margin: 0, padding: 0, overflow: 'hidden' },
                '& img': { maxWidth: '100% !important', height: 'auto !important' },
                '& table': { width: '100% !important', tableLayout: 'fixed !important' },
                '& *[style*="position: absolute"]': { position: 'relative !important' },
                '& .email-container': { width: '85% !important' },
              }}
              dangerouslySetInnerHTML={{ __html: template.contents }}
            />
          </Box>
        </Box>

        {template?.is_default && (
          <Box sx={(theme) => ({ position: 'absolute', top: 8, left: 8, width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.theme.primary[800], zIndex: 3 })} />
        )}
      </Box>

      {template?.name && <Typography type='s3' weight='medium' color='theme.secondary.1000' align='center' sx={{ width, mt: 0.5 }}>{template?.name}</Typography>}

      {showControls && (
        <Fade in={isHovered} timeout={200}>
          <Box sx={{ position: 'absolute', top: -8, right: -8, display: 'flex', gap: 0.5, zIndex: 4, backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid', borderColor: 'grey.300', borderRadius: 2, padding: 0.5, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)' }}>
            <Tooltip title={`Zoom In (${Math.round(currentScale * 100)}%)`} arrow>
              <span><IconButton size="small" onClick={handleZoomIn} disabled={!canZoomIn}><ZoomIn fontSize="small" /></IconButton></span>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={`Zoom Out (${Math.round(currentScale * 100)}%)`} arrow>
              <span><IconButton size="small" onClick={handleZoomOut} disabled={!canZoomOut}><ZoomOut fontSize="small" /></IconButton></span>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Tooltip title="Refresh Preview" arrow>
              <IconButton size="small" onClick={handleRefresh}><Refresh fontSize="small" /></IconButton>
            </Tooltip>
            {onDelete && <>
              <Divider orientation="vertical" flexItem />
              <Tooltip title={canDelete ? "Delete Template" : "Cannot delete default template"} arrow>
                <span><IconButton size="small" onClick={handleDelete} disabled={!canDelete}><Trash fontSize="small" /></IconButton></span>
              </Tooltip>
            </>}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default HtmlThumbnailPreviewFull;